import { parseBahtToSatang } from '@pos/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { PromoPreviewCard } from '../../components/PromoPreviewCard';
import { listBranches } from '../../data/fetchers';
import { upsertPromotion } from '../../data/mutators';
import { canEditPromotions } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type { Promotion } from '../../types/domain';
import { PromotionProductSelector } from './PromotionProductSelector';
import { SettingsHeader } from './SettingsHeader';

const blank = (): Promotion => ({
  id: `pr-${crypto.randomUUID()}`,
  name: '',
  type: 'percent',
  value: 0,
  status: 'scheduled',
  startDate: '',
  endDate: '',
  scope: 'all',
  branchIds: [],
  productIds: [],
});

const inputCls =
  'h-10 px-3 rounded-btn border border-line bg-surface text-body disabled:bg-bg-warm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all duration-200';

export function PromotionFormView({
  promo,
  onClose,
}: {
  promo: Promotion | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditPromotions(user) : false;
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const [form, setForm] = useState<Promotion>(blank);
  const submitting = useRef(false);
  const requiresProducts =
    form.type === 'bogo' ||
    form.type === 'qty_tier' ||
    form.type === 'fixed_price';
  const moneyValue = ['baht', 'fixed_price', 'qty_tier'].includes(form.type);
  const moneyLimit = form.type === 'bill_percent';
  const limitLabel =
    form.type === 'bogo'
      ? 'จำนวนชิ้นฟรีสูงสุดต่อบิล'
      : form.type === 'qty_tier'
        ? 'จำนวนชุดสูงสุดต่อบิล'
        : moneyLimit
          ? 'ส่วนลดสูงสุดต่อบิล (บาท)'
          : 'จำนวนชิ้นที่ลดสูงสุดต่อบิล';
  const valueLabel =
    form.type === 'qty_tier'
      ? 'ส่วนลดต่อชุด (บาท)'
      : form.type === 'fixed_price'
        ? 'ราคาพิเศษต่อชิ้น (บาท)'
        : form.type === 'percent' || form.type === 'bill_percent'
          ? 'ส่วนลด (%)'
          : 'ส่วนลด (บาท)';
  const tierInvalid =
    form.type === 'qty_tier' &&
    (!(form.minQty && form.minQty >= 1) || form.value <= 0);
  const scopeInvalid = form.scope === 'specific' && form.branchIds.length === 0;
  const percentageInvalid =
    (form.type === 'percent' || form.type === 'bill_percent') &&
    (!Number.isFinite(form.value) || form.value < 0 || form.value > 100);
  const dateInvalid =
    !!form.startDate && !!form.endDate && form.endDate < form.startDate;
  const bogoFreeQty = Math.max(1, Math.floor(form.value || 1));
  const bogoBuyQty = Math.max(1, (form.minQty ?? 2) - bogoFreeQty);

  useEffect(() => {
    setForm(promo ? { ...promo } : blank());
  }, [promo]);

  const mutation = useMutation({
    mutationFn: upsertPromotion,
    onSettled: () => {
      submitting.current = false;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['promotions'] });
      onClose();
    },
  });

  const set = <K extends keyof Promotion>(k: K, v: Promotion[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const toggleBranch = (id: string) =>
    setForm((f) => ({
      ...f,
      branchIds: f.branchIds.includes(id)
        ? f.branchIds.filter((x) => x !== id)
        : [...f.branchIds, id],
    }));
  const numOrUndef = (v: string) => (v === '' ? undefined : Number(v));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (
      !canEdit ||
      submitting.current ||
      mutation.isPending ||
      !form.name.trim() ||
      (requiresProducts && (form.productIds?.length ?? 0) === 0) ||
      tierInvalid ||
      scopeInvalid ||
      percentageInvalid ||
      dateInvalid
    )
      return;
    submitting.current = true;
    mutation.mutate(form);
  };

  return (
    <div className="bo-fade mx-auto w-full max-w-6xl">
      <SettingsHeader
        title={promo ? 'แก้ไขโปรโมชั่น' : 'เพิ่มโปรโมชั่น'}
        subtitle={
          canEdit
            ? 'กำหนดรายละเอียดเงื่อนไขและประเภทส่วนลด'
            : 'อ่านอย่างเดียว (ไม่มีสิทธิ์แก้ไข)'
        }
        status={{
          label: promo ? 'กำลังแก้ไขโปรโมชั่น' : 'โปรโมชั่นใหม่',
          tone: canEdit ? 'neutral' : 'warn',
        }}
      />

      <form
        onSubmit={onSubmit}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start"
      >
        {/* Left Column: Form Details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Card 1: Basic Info */}
          <Card title="ข้อมูลโปรโมชั่น">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex flex-col gap-1.5 sm:col-span-2">
                <span className="text-sm-label font-bold text-ink-2">
                  ชื่อโปรโมชั่น
                </span>
                <input
                  aria-label="ชื่อโปรโมชั่น"
                  disabled={!canEdit}
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className={inputCls}
                  placeholder="เช่น ลด 10% ต้อนรับเปิดเทอม, ซื้อ 1 แถม 1 ชานมไข่มุก"
                  required
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-bold text-ink-2">
                  ประเภทส่วนลด
                </span>
                <div className="relative">
                  <select
                    aria-label="ประเภท"
                    disabled={!canEdit}
                    value={form.type}
                    onChange={(e) => {
                      const nextType = e.target.value as Promotion['type'];
                      if (nextType === form.type) return;
                      // Values and quantity limits have different meanings by type.
                      setForm((f) => ({
                        ...f,
                        type: nextType,
                        value: nextType === 'bogo' ? 1 : 0,
                        minQty:
                          nextType === 'bogo'
                            ? 2
                            : nextType === 'qty_tier'
                              ? 1
                              : undefined,
                        perBillLimit: undefined,
                        productIds:
                          nextType === 'bill_percent' ? [] : f.productIds,
                      }));
                    }}
                    className="w-full h-10 pl-3 pr-8 rounded-btn border border-line bg-surface text-body disabled:bg-bg-warm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 appearance-none cursor-pointer transition-all duration-200"
                  >
                    <option value="percent">ลดเป็นเปอร์เซ็นต์ (%)</option>
                    <option value="bill_percent">
                      ลดเปอร์เซ็นต์ทั้งบิล (%)
                    </option>
                    <option value="baht">ลดเป็นบาท (฿)</option>
                    <option value="fixed_price">ราคาพิเศษต่อชิ้น (฿)</option>
                    <option value="bogo">
                      ซื้อ {form.type === 'bogo' ? bogoBuyQty : 1} แถม{' '}
                      {form.type === 'bogo' ? bogoFreeQty : 1} (BOGO)
                    </option>
                    <option value="qty_tier">ซื้อครบจำนวน รับส่วนลด</option>
                  </select>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none text-ink-3">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </label>

              {form.type !== 'bogo' && (
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-bold text-ink-2">
                    {valueLabel}
                  </span>
                  <input
                    aria-label="มูลค่า"
                    disabled={!canEdit}
                    type="number"
                    min={0}
                    max={
                      form.type === 'percent' || form.type === 'bill_percent'
                        ? 100
                        : undefined
                    }
                    step={moneyValue ? '0.01' : '1'}
                    value={moneyValue ? form.value / 100 : form.value}
                    onChange={(e) =>
                      set(
                        'value',
                        moneyValue
                          ? parseBahtToSatang(e.target.value)
                          : Number(e.target.value),
                      )
                    }
                    className={`${inputCls} num`}
                    placeholder={
                      form.type === 'qty_tier'
                        ? 'เช่น 50.00'
                        : moneyValue
                          ? 'เช่น 50.00'
                          : 'เช่น 10'
                    }
                    required
                  />
                </label>
              )}

              <div
                className={`grid grid-cols-1 ${form.type === 'bogo' || form.type === 'baht' ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-4 sm:col-span-2 border-y border-line-2 py-4`}
              >
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-bold text-ink-2">
                    ยอดซื้อขั้นต่ำ (บาท)
                  </span>
                  <input
                    aria-label="ยอดขั้นต่ำ (บาท)"
                    disabled={!canEdit}
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.minBaht != null ? form.minBaht / 100 : ''}
                    onChange={(e) =>
                      set(
                        'minBaht',
                        e.target.value === ''
                          ? undefined
                          : parseBahtToSatang(e.target.value),
                      )
                    }
                    className={`${inputCls} num`}
                    placeholder="เช่น 100"
                  />
                </label>

                {form.type !== 'bogo' && (
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm-label font-bold text-ink-2">
                      {form.type === 'qty_tier'
                        ? 'จำนวนชิ้นต่อชุด'
                        : 'จำนวนสินค้าขั้นต่ำ (ชิ้น)'}
                    </span>
                    <input
                      aria-label="จำนวนขั้นต่ำ"
                      disabled={!canEdit}
                      type="number"
                      min={form.type === 'qty_tier' ? 1 : 0}
                      value={form.minQty ?? ''}
                      onChange={(e) =>
                        set('minQty', numOrUndef(e.target.value))
                      }
                      className={`${inputCls} num`}
                      placeholder={
                        form.type === 'qty_tier' ? 'เช่น 24' : 'เช่น 2'
                      }
                    />
                  </label>
                )}

                {form.type !== 'baht' && (
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm-label font-bold text-ink-2">
                      {limitLabel}
                    </span>
                    <input
                      aria-label={limitLabel}
                      disabled={!canEdit}
                      type="number"
                      min={moneyLimit ? 0.01 : 1}
                      step={moneyLimit ? '0.01' : '1'}
                      value={
                        form.perBillLimit != null
                          ? moneyLimit
                            ? form.perBillLimit / 100
                            : form.perBillLimit
                          : ''
                      }
                      onChange={(e) =>
                        set(
                          'perBillLimit',
                          e.target.value === ''
                            ? undefined
                            : moneyLimit
                              ? parseBahtToSatang(e.target.value)
                              : Number(e.target.value),
                        )
                      }
                      className={`${inputCls} num`}
                      placeholder="ไม่จำกัด"
                    />
                  </label>
                )}
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-bold text-ink-2">
                  สถานะโปรโมชั่น
                </span>
                <div className="relative">
                  <select
                    aria-label="สถานะ"
                    disabled={!canEdit}
                    value={form.status}
                    onChange={(e) =>
                      set('status', e.target.value as Promotion['status'])
                    }
                    className="w-full h-10 pl-3 pr-8 rounded-btn border border-line bg-surface text-body disabled:bg-bg-warm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 appearance-none cursor-pointer transition-all duration-200"
                  >
                    <option value="active">กำลังใช้งาน (Active)</option>
                    <option value="scheduled">
                      ตั้งเวลาเปิดอัตโนมัติ (Scheduled)
                    </option>
                    <option value="ended">สิ้นสุดโปรโมชั่น (Ended)</option>
                  </select>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none text-ink-3">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </label>

              <div className="grid grid-cols-2 gap-3 sm:col-span-1">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-bold text-ink-2">
                    วันที่เริ่ม
                  </span>
                  <input
                    aria-label="วันที่เริ่ม"
                    disabled={!canEdit}
                    type="date"
                    value={form.startDate}
                    onChange={(e) => set('startDate', e.target.value)}
                    className={inputCls}
                    required
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-bold text-ink-2">
                    วันที่สิ้นสุด
                  </span>
                  <input
                    aria-label="วันที่สิ้นสุด"
                    min={form.startDate || undefined}
                    disabled={!canEdit}
                    type="date"
                    value={form.endDate}
                    onChange={(e) => set('endDate', e.target.value)}
                    className={inputCls}
                    required
                  />
                </label>
              </div>
              {percentageInvalid && (
                <p role="alert" className="text-sm text-danger sm:col-span-2">
                  เปอร์เซ็นต์ต้องอยู่ระหว่าง 0 ถึง 100
                </p>
              )}
              {dateInvalid && (
                <p role="alert" className="text-sm text-danger sm:col-span-2">
                  วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่ม
                </p>
              )}
            </div>
          </Card>

          {form.type !== 'bill_percent' && (
            <Card title="สินค้าที่ร่วมรายการ">
              <div className="flex flex-col gap-3">
                {requiresProducts && (
                  <p className="text-sm text-ink-3">
                    {form.type === 'qty_tier'
                      ? 'เลือกสินค้าอย่างน้อย 1 รายการ โดยนับจำนวนรวมกันในบิล รับส่วนลดซ้ำได้ทุกครั้งที่ซื้อครบจำนวน เช่น ทุก 24 ชิ้น ลด 50 บาท ซื้อ 48 ชิ้น ลด 100 บาท'
                      : form.type === 'fixed_price'
                        ? 'โปรราคาพิเศษต้องเลือกสินค้าอย่างน้อย 1 รายการ'
                        : `โปรซื้อ ${bogoBuyQty} แถม ${bogoFreeQty} ต้องเลือกสินค้าอย่างน้อย 1 รายการ คละสินค้าที่เลือกได้ โดยให้ชิ้นที่ราคาต่ำที่สุดเป็นของแถม`}
                  </p>
                )}
                <PromotionProductSelector
                  selectedIds={form.productIds ?? []}
                  disabled={!canEdit}
                  onChange={(productIds) => set('productIds', productIds)}
                />
                {form.type === 'baht' && (
                  <p className="text-sm text-ink-3">
                    ลดตามจำนวนเงินที่ระบุหนึ่งครั้งต่อบิล จากสินค้าที่เลือก
                    หรือทั้งบิลหากไม่เลือกสินค้า
                  </p>
                )}
              </div>
            </Card>
          )}

          {/* Card 2: Scope of branches */}
          <Card title="ขอบเขตสาขาที่ร่วมรายการ">
            <div className="flex flex-col gap-3">
              <span className="text-sm-label font-bold text-ink-2">
                ขอบเขตการใช้งาน
              </span>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="scope"
                    disabled={!canEdit}
                    checked={form.scope === 'all'}
                    onChange={() => set('scope', 'all')}
                    className="w-4 h-4 text-primary focus:ring-primary/20 accent-primary cursor-pointer"
                  />
                  <span className="text-body font-semibold text-ink-2">
                    ร่วมรายการทุกสาขา
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="scope"
                    disabled={!canEdit}
                    checked={form.scope === 'specific'}
                    onChange={() => set('scope', 'specific')}
                    className="w-4 h-4 text-primary focus:ring-primary/20 accent-primary cursor-pointer"
                  />
                  <span className="text-body font-semibold text-ink-2">
                    ร่วมรายการเฉพาะบางสาขา
                  </span>
                </label>
              </div>

              {form.scope === 'specific' && (
                <div className="flex flex-wrap gap-4 pt-2 border-t border-line-2/50 mt-1">
                  {branches.map((b) => (
                    <label
                      key={b.id}
                      className="flex items-center gap-2 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        disabled={!canEdit}
                        checked={form.branchIds.includes(b.id)}
                        onChange={() => toggleBranch(b.id)}
                        className="w-4.5 h-4.5 rounded border-line text-primary focus:ring-primary/30 accent-primary cursor-pointer"
                      />
                      <span className="text-body font-semibold text-ink-2">
                        {b.short}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {scopeInvalid && (
                <p role="alert" className="text-sm text-danger">
                  กรุณาเลือกอย่างน้อย 1 สาขา
                </p>
              )}
            </div>
          </Card>

          {/* Footer Actions */}
          {mutation.isError && (
            <p role="alert" className="text-sm text-danger">
              บันทึกโปรโมชั่นไม่สำเร็จ กรุณาลองอีกครั้ง
            </p>
          )}
          <div className="flex gap-2 pt-2">
            <Btn kind="default" type="button" onClick={onClose}>
              ยกเลิก
            </Btn>
            {canEdit && (
              <Btn
                kind="primary"
                type="submit"
                disabled={
                  mutation.isPending ||
                  percentageInvalid ||
                  dateInvalid ||
                  (requiresProducts && (form.productIds?.length ?? 0) === 0) ||
                  tierInvalid ||
                  scopeInvalid
                }
              >
                บันทึก
              </Btn>
            )}
          </div>
        </div>

        {/* Right Column: Visual POS Preview */}
        <div className="lg:col-span-1 sticky top-6">
          <Card
            title="ตัวอย่างบนเครื่อง POS"
            className="shadow-md bg-surface/90 border border-line-2"
          >
            <div className="flex flex-col items-center gap-4 py-3">
              <p className="text-xs text-ink-3 text-center leading-relaxed">
                จำลองหน้าต่างโปรโมชั่นที่แสดงผลบน
                <br />
                หน้าจอฝั่งเครื่องแคชเชียร์ POS
              </p>

              {/* Promo Preview Card Container */}
              <div className="w-full bg-bg-warm border border-line-2 rounded-xl p-5 flex items-center justify-center min-h-[140px] shadow-inner">
                <PromoPreviewCard promo={form} />
              </div>

              <div className="text-sm-label text-ink-3 flex flex-col gap-1 w-full bg-bg/50 p-3 rounded-btn border border-line-2 select-none">
                <div className="flex justify-between">
                  <span>ขอบเขต:</span>
                  <span className="font-bold text-ink-2">
                    {form.scope === 'all'
                      ? 'ทุกสาขา'
                      : `เฉพาะสาขาที่กำหนด (${form.branchIds.length})`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>สถานะ:</span>
                  <span
                    className={`font-bold ${form.status === 'active' ? 'text-ok' : form.status === 'scheduled' ? 'text-primary' : 'text-ink-4'}`}
                  >
                    {form.status === 'active'
                      ? 'เริ่มใช้งานทันที'
                      : form.status === 'scheduled'
                        ? 'ตั้งเวลารอกำหนด'
                        : 'โปรโมชั่นสิ้นสุด'}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </form>
    </div>
  );
}
